<template>
  <div class="ev-config-panel" v-loading="loading">
    <div class="config-toolbar">
      <span class="hint">修改参数后请点击「应用配置」写入 Redis + Mongo；首次无配置时展示前端默认种子值</span>
      <div class="toolbar-actions">
        <el-tag v-if="saving" size="mini" type="warning">保存中...</el-tag>
        <el-tag v-else-if="dirty" size="mini" type="info">待保存</el-tag>
        <el-tag v-else-if="usingSeed" size="mini" type="warning">Map 未入库（请应用）</el-tag>
        <el-tag v-else size="mini" type="success">已同步</el-tag>
        <el-button size="mini" type="primary" :loading="saving" :disabled="!dirty" @click="saveConfig">应用配置</el-button>
        <el-button size="mini" :disabled="saving" @click="exportConfig">导出</el-button>
        <el-button size="mini" :disabled="saving" @click="pickImportFile">导入</el-button>
        <el-button size="mini" :loading="resetting" @click="onReset">恢复默认</el-button>
        <input ref="configFile" type="file" accept="application/json,.json" class="config-file" @change="onImportFile">
      </div>
    </div>

    <div class="config-scroll">
      <el-collapse v-model="activeSections">
        <el-collapse-item title="W_loc — 城市权重" name="wLoc">
          <p class="section-desc">默认区域权重。某一天要改某个区域，用下面的按日期覆盖。</p>
          <el-table :data="areaIds.map(id => ({ areaId: id }))" size="mini" border class="compact-table">
            <el-table-column label="区域" width="160">
              <template slot-scope="scope">{{ areaLabel(scope.row.areaId) }}</template>
            </el-table-column>
            <el-table-column label="权重" width="140">
              <template slot-scope="scope">
                <el-input-number :value="wLocValue(scope.row.areaId)" :step="0.1" :precision="2" :controls="false"
                  size="mini" @change="v => updateWLoc(scope.row.areaId, v)"></el-input-number>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>

        <el-collapse-item title="r_pts — 区域积分率" name="rPts">
          <el-table :data="areaIds.map(id => ({ areaId: id }))" size="mini" border class="compact-table">
            <el-table-column label="区域" width="160">
              <template slot-scope="scope">{{ areaLabel(scope.row.areaId) }}</template>
            </el-table-column>
            <el-table-column label="积分率" width="140">
              <template slot-scope="scope">
                <el-input-number :value="rPtsValue(scope.row.areaId)" :step="1" :precision="1" :controls="false"
                  size="mini" @change="v => updateRPts(scope.row.areaId, v)"></el-input-number>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>

        <el-collapse-item title="W_time — 全局时段权重" name="wTimeGlobal">
          <div class="section-actions">
            <el-button size="mini" icon="el-icon-plus" @click="addWTimeHour">添加小时</el-button>
          </div>
          <el-table :data="wTimeHourRowsLocal" size="mini" border class="compact-table" row-key="uid">
            <el-table-column label="小时" width="120">
              <template slot-scope="scope">
                <el-input :value="scope.row.hour" size="mini" type="number" min="0" max="23"
                  @input="v => setWTimeHourField(scope.$index, 'hour', v)"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="权重" width="140">
              <template slot-scope="scope">
                <el-input :value="scope.row.weight" size="mini" type="number" step="0.1"
                  @input="v => setWTimeHourField(scope.$index, 'weight', v)"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="80">
              <template slot-scope="scope">
                <el-button type="text" size="mini" @click="removeWTimeHour(scope.$index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>

        <el-collapse-item title="W_time — 分区域时段权重" name="wTimeArea">
          <div class="section-actions">
            <el-button size="mini" icon="el-icon-plus" @click="addWTimeAreaHour">添加</el-button>
          </div>
          <el-table :data="wTimeAreaHourRowsLocal" size="mini" border class="compact-table" row-key="uid">
            <el-table-column label="区域" width="160">
              <template slot-scope="scope">
                <el-select :value="scope.row.areaId" size="mini" placeholder="区域"
                  @change="v => setWTimeAreaField(scope.$index, 'areaId', v)">
                  <el-option v-for="item in areaOptions" :key="item.id" :label="areaLabel(item.id)"
                    :value="item.id"></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="小时" width="100">
              <template slot-scope="scope">
                <el-input :value="scope.row.hour" size="mini" type="number" min="0" max="23"
                  @input="v => setWTimeAreaField(scope.$index, 'hour', v)"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="权重" width="140">
              <template slot-scope="scope">
                <el-input :value="scope.row.weight" size="mini" type="number" step="0.1"
                  @input="v => setWTimeAreaField(scope.$index, 'weight', v)"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="80">
              <template slot-scope="scope">
                <el-button type="text" size="mini" @click="removeWTimeAreaHour(scope.$index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>

        <el-collapse-item title="W_loc — 按日期覆盖" name="wLocByDate">
          <p class="section-desc">指定日期、区域的权重优先于默认城市权重。没列出的日期和区域仍用默认 W_loc。</p>
          <div class="section-actions">
            <el-button size="mini" icon="el-icon-plus" @click="addWLocByDate">添加</el-button>
          </div>
          <el-table :data="wLocByDateRowsLocal" size="mini" border class="compact-table" row-key="uid">
            <el-table-column label="日期" width="180">
              <template slot-scope="scope">
                <el-date-picker :value="scope.row.date" type="date" value-format="yyyy-MM-dd" size="mini"
                  placeholder="选择日期" style="width: 160px"
                  @input="v => setWLocByDateField(scope.$index, 'date', v)"></el-date-picker>
              </template>
            </el-table-column>
            <el-table-column label="区域" width="160">
              <template slot-scope="scope">
                <el-select :value="scope.row.areaId" size="mini" placeholder="区域"
                  @change="v => setWLocByDateField(scope.$index, 'areaId', v)">
                  <el-option v-for="item in areaOptions" :key="item.id" :label="areaLabel(item.id)"
                    :value="item.id"></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="权重" width="140">
              <template slot-scope="scope">
                <el-input :value="scope.row.weight" size="mini" type="number" step="0.1"
                  @input="v => setWLocByDateField(scope.$index, 'weight', v)"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="80">
              <template slot-scope="scope">
                <el-button type="text" size="mini" @click="removeWLocByDate(scope.$index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>

        <el-collapse-item title="W_date — 日期权重" name="wDate">
          <p class="section-desc">指定日期优先；未配置则工作日 / 周末 / 节假日分档。与按日期的区域权重相乘。</p>
          <el-form label-width="120px" size="mini" class="mu-form">
            <el-form-item label="工作日">
              <el-input-number v-model="config.wDateWeekday" :min="0" :max="5" :step="0.1" :precision="2"
                :controls="false" @change="markDirty"></el-input-number>
            </el-form-item>
            <el-form-item label="周末">
              <el-input-number v-model="config.wDateWeekend" :min="0" :max="5" :step="0.1" :precision="2"
                :controls="false" @change="markDirty"></el-input-number>
            </el-form-item>
            <el-form-item label="节假日">
              <el-input-number v-model="config.wDateHoliday" :min="0" :max="5" :step="0.1" :precision="2"
                :controls="false" @change="markDirty"></el-input-number>
              <span class="form-hint">仅当运行时节假日名单命中且该日没有单独覆盖时生效</span>
            </el-form-item>
          </el-form>
          <div class="section-actions">
            <el-button size="mini" icon="el-icon-plus" @click="addWDateOverride">添加指定日期</el-button>
          </div>
          <el-table :data="wDateOverrideRowsLocal" size="mini" border class="compact-table" row-key="uid">
            <el-table-column label="日期" width="180">
              <template slot-scope="scope">
                <el-date-picker :value="scope.row.date" type="date" value-format="yyyy-MM-dd" size="mini"
                  placeholder="选择日期" style="width: 160px"
                  @input="v => setWDateOverrideField(scope.$index, 'date', v)"></el-date-picker>
              </template>
            </el-table-column>
            <el-table-column label="权重" width="140">
              <template slot-scope="scope">
                <el-input :value="scope.row.weight" size="mini" type="number" step="0.1"
                  @input="v => setWDateOverrideField(scope.$index, 'weight', v)"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="80">
              <template slot-scope="scope">
                <el-button type="text" size="mini" @click="removeWDateOverride(scope.$index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>

        <el-collapse-item title="当日积分上限 — 按司机按日期" name="dayMaxPoints">
          <p class="section-desc">只填写要改的司机和日期。没列出的仍用默认日上限。和排班表读写同一份数据，保存时会带上这里已有的行。</p>
          <div class="section-actions">
            <el-button size="mini" icon="el-icon-plus" @click="addDayMaxPoints">添加</el-button>
          </div>
          <el-table :data="dayMaxPointsRowsLocal" size="mini" border class="compact-table" row-key="uid">
            <el-table-column label="日期" width="180">
              <template slot-scope="scope">
                <el-date-picker :value="scope.row.date" type="date" value-format="yyyy-MM-dd" size="mini"
                  placeholder="选择日期" style="width: 160px"
                  @input="v => setDayMaxPointsField(scope.$index, 'date', v)"></el-date-picker>
              </template>
            </el-table-column>
            <el-table-column label="司机编号" width="160">
              <template slot-scope="scope">
                <el-select :value="scope.row.driverId" size="mini" filterable placeholder="编号"
                  @change="v => setDayMaxPointsField(scope.$index, 'driverId', v)">
                  <el-option v-for="item in driverSelectOptions" :key="item.id" :label="item.no"
                    :value="item.id"></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="最大积分" width="140">
              <template slot-scope="scope">
                <el-input :value="scope.row.points" size="mini" type="number" min="0"
                  @input="v => setDayMaxPointsField(scope.$index, 'points', v)"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="80">
              <template slot-scope="scope">
                <el-button type="text" size="mini" @click="removeDayMaxPoints(scope.$index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>

        <el-collapse-item title="θ — ROI 断点 (roiBreakpoints)" name="roi">
          <breakpoint-editor v-model="config.roiBreakpoints" @change="markDirty" />
        </el-collapse-item>

        <el-collapse-item title="γ — Gamma 断点 (空驶发生概率)" name="gamma">
          <breakpoint-editor v-model="config.gammaBreakpoints" @change="markDirty" />
        </el-collapse-item>

        <el-collapse-item title="β — Beta 断点 (拼车成单衰减系数)" name="beta">
          <breakpoint-editor v-model="config.betaBreakpoints" @change="markDirty" />
        </el-collapse-item>

        <el-collapse-item title="订单热度 — 订单有多旺，再按剩车打折" name="mu">
          <el-form label-width="160px" size="mini" class="mu-form">
            <el-form-item label="订单热度下限">
              <el-input-number v-model="config.muFloor" :min="0" :max="5" :step="0.1" :precision="2" :controls="false"
                @change="markDirty"></el-input-number>
              <span class="form-hint">折成 0 车时的订单热度。营业时间内每个半点至少按 1 车算，一般不会落到这里</span>
            </el-form-item>
            <el-form-item label="正常车数">
              <el-input-number v-model="config.muNormalCars" :min="1" :max="20" :step="1" :controls="false"
                @change="markDirty"></el-input-number>
              <span class="form-hint">订单热度升到 1 的车数。座位取这个整点已订、和窗起点库存目标×每车座位里更大的那个，再向上取整成车</span>
            </el-form-item>
            <el-form-item label="封顶车数">
              <el-input-number v-model="config.muHotCars" :min="2" :max="30" :step="1" :controls="false"
                @change="markDirty"></el-input-number>
              <span class="form-hint">订单热度升到上限的车数。过了正常车数后升得更快。默认 2 车为 1，6 车封顶</span>
            </el-form-item>
            <el-form-item label="订单热度上限">
              <el-input-number v-model="config.muCeil" :min="1" :max="5" :step="0.1" :precision="2" :controls="false"
                @change="markDirty"></el-input-number>
              <span class="form-hint">达到封顶车数之后，订单热度不再升高</span>
            </el-form-item>
            <el-form-item label="默认剩车缓冲">
              <el-input-number v-model="config.muSurplusBufferK" :min="0" :max="20" :step="1" :controls="false"
                @change="markDirty"></el-input-number>
              <span class="form-hint">没单独配置的区域用这个数。有效供给比库存目标多出的车不超过它时，热度不打折</span>
            </el-form-item>
            <el-form-item label="区域剩车缓冲">
              <el-table :data="areaIds.map(id => ({ areaId: id }))" size="mini" border class="compact-table">
                <el-table-column label="区域" min-width="140">
                  <template slot-scope="scope">{{ areaLabel(scope.row.areaId) }}</template>
                </el-table-column>
                <el-table-column label="K" width="140">
                  <template slot-scope="scope">
                    <el-input-number :value="muSurplusBufferKValue(scope.row.areaId)" :min="0" :max="20" :step="1"
                      :controls="false" size="mini"
                      @change="v => updateMuSurplusBufferK(scope.row.areaId, v)"></el-input-number>
                  </template>
                </el-table-column>
              </el-table>
              <span class="form-hint">没填的区域用上面的默认值。多出的车不超过该区域的缓冲时，热度不打折</span>
            </el-form-item>
            <el-form-item label="分档上限">
              <el-input-number v-model="config.muSurplusBinCapK" :min="0" :max="50" :step="1" :controls="false"
                @change="markDirty"></el-input-number>
              <span class="form-hint">多出来的车按档打折。这个数以内每 2 辆一档，再往后档变宽</span>
            </el-form-item>
            <el-form-item label="剩车打折斜率">
              <el-input-number v-model="config.muSurplusLambda" :min="0" :max="2" :step="0.01" :precision="2"
                :controls="false" @change="markDirty"></el-input-number>
              <span class="form-hint">超过缓冲之后，多出来的车越多，热度打得越低。0.4 时每多 1 车大约再乘 0.67</span>
            </el-form-item>
            <el-form-item label="打折下限">
              <el-input-number v-model="config.muSurplusFloor" :min="0" :max="1" :step="0.05" :precision="2"
                :controls="false" @change="markDirty"></el-input-number>
              <span class="form-hint">热度最多打到这个比例。0 表示车堆很多时可以接近打没</span>
            </el-form-item>
          </el-form>
        </el-collapse-item>
        <el-collapse-item title="tripCost — 路线行程成本" name="tripCost">
          <el-form size="mini" label-width="140px" class="ev-form" style="margin-bottom: 8px">
            <el-form-item label="闲置整车成本">
              <el-input-number v-model="config.unusedCarCost" :min="0" :max="1000" :step="1" :precision="1"
                :controls="false" @change="markDirty"></el-input-number>
              <span class="form-hint">已停用。求解不再读取这项。空车只允许开去还没达到库存目标的区域</span>
            </el-form-item>
          </el-form>
          <el-table :data="tripCostRows" size="mini" border style="width: 100%">
            <el-table-column prop="pathId" label="路线ID" width="90"></el-table-column>
            <el-table-column prop="name" label="路线" min-width="120"></el-table-column>
            <el-table-column label="单趟成本" min-width="140">
              <template slot-scope="scope">
                <el-input-number :value="tripCostValue(scope.row.pathId)" :min="0" :step="1" :precision="1"
                  :controls="false" @change="(v) => setTripCost(scope.row.pathId, v)"></el-input-number>
              </template>
            </el-table-column>
          </el-table>
          <p class="form-hint" style="margin-top: 8px">每发一趟扣一次（空座/载客同价）。单趟成本空表示用路线 valuePoints 回退。</p>
        </el-collapse-item>
      </el-collapse>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { getEvTunableConfig, applyEvTunableConfig } from "@/api/ev_solver";
import { getDriverList } from "@/api/driver";
import { createSeedTunableConfig, mergeTunableWithSeed, hasUnpersistedMaps } from "../../utils/evTunableDefaults";
import BreakpointEditor from "./BreakpointEditor";

const DEFAULT_AREA_IDS = [1, 2, 3, 4];
const TUNABLE_KEYS = [
  "wLoc", "rPtsByArea", "wTimeByHour", "wTimeByAreaHour",
  "roiBreakpoints", "gammaBreakpoints", "betaBreakpoints",
  "muFloor", "muCeil", "muNormalCars", "muHotCars",
  "muSurplusBufferK", "muSurplusBufferKByArea", "muSurplusBinCapK", "muSurplusLambda", "muSurplusFloor",
  "unusedCarCost", "tripCostByRoute", "sellableRewardByRoute", "sellableRewardCars",
  "wLocByDate", "wDateWeekday", "wDateWeekend", "wDateHoliday", "wDateByDate", "dayMaxPointsByDate"
];

function unwrapConfig(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  if (raw.wLoc == null && raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    return raw.data;
  }
  return raw;
}

function normalizeBreakpoints(list) {
  if (!Array.isArray(list)) return [];
  return list.map((bp) => {
    if (Array.isArray(bp)) {
      return { minutes: bp[0], value: bp[1] };
    }
    if (bp && bp.minutes != null && bp.value != null) {
      return { minutes: bp.minutes, value: bp.value };
    }
    return null;
  }).filter(Boolean);
}

function pickTunableConfig(raw) {
  const src = unwrapConfig(raw);
  if (!src) return null;
  const out = {};
  TUNABLE_KEYS.forEach((key) => {
    if (src[key] !== undefined) {
      out[key] = src[key];
    }
  });
  ["roiBreakpoints", "gammaBreakpoints", "betaBreakpoints"].forEach((key) => {
    if (out[key] !== undefined) {
      out[key] = normalizeBreakpoints(out[key]);
    }
  });
  return Object.keys(out).length ? out : null;
}
let rowUid = 0;

function nextRowUid() {
  rowUid += 1;
  return `wtime_${rowUid}`;
}

function emptyConfig() {
  const seed = createSeedTunableConfig();
  return {
    wLoc: {},
    rPtsByArea: {},
    wTimeByHour: {},
    wTimeByAreaHour: {},
    roiBreakpoints: [],
    gammaBreakpoints: [],
    betaBreakpoints: [],
    muFloor: seed.muFloor,
    muCeil: seed.muCeil,
    muNormalCars: seed.muNormalCars,
    muHotCars: seed.muHotCars,
    muSurplusBufferK: seed.muSurplusBufferK,
    muSurplusBufferKByArea: {},
    muSurplusBinCapK: seed.muSurplusBinCapK,
    muSurplusLambda: seed.muSurplusLambda,
    muSurplusFloor: seed.muSurplusFloor,
    unusedCarCost: seed.unusedCarCost,
    tripCostByRoute: {},
    wLocByDate: {},
    wDateWeekday: seed.wDateWeekday,
    wDateWeekend: seed.wDateWeekend,
    wDateHoliday: seed.wDateHoliday,
    wDateByDate: {},
    dayMaxPointsByDate: {}
  };
}

export default {
  components: { BreakpointEditor },

  data() {
    return {
      loading: false,
      saving: false,
      resetting: false,
      dirty: false,
      usingSeed: false,
      loadSeq: 0,
      wTimeInitialized: false,
      activeSections: ["wLoc", "wLocByDate", "wDate", "dayMaxPoints", "rPts", "wTimeGlobal", "wTimeArea", "roi", "gamma", "beta", "mu", "tripCost"],
      config: emptyConfig(),
      wTimeHourRowsLocal: [],
      wTimeAreaHourRowsLocal: [],
      wLocByDateRowsLocal: [],
      wDateOverrideRowsLocal: [],
      dayMaxPointsRowsLocal: [],
      driverOptions: []
    };
  },

  computed: {
    ...mapState({
      areaOpts: (state) => state.areaOpts,
      pathList: (state) => state.pathList
    }),

    areaOptions() {
      if (this.areaOpts && this.areaOpts.length) {
        return this.areaOpts;
      }
      return DEFAULT_AREA_IDS.map((id) => ({ id, name: `区域${id}` }));
    },

    areaIds() {
      const fromConfig = [
        ...Object.keys(this.config.wLoc || {}),
        ...Object.keys(this.config.rPtsByArea || {}),
        ...Object.keys(this.config.wTimeByAreaHour || {}),
        ...Object.keys(this.config.muSurplusBufferKByArea || {})
      ].map(Number);
      const ids = [...new Set([...DEFAULT_AREA_IDS, ...fromConfig])];
      return ids.sort((a, b) => a - b);
    },

    driverSelectOptions() {
      const map = new Map();
      (this.driverOptions || []).forEach((driver) => {
        if (driver == null || driver.id == null || !driver.no) return;
        map.set(String(driver.id), { id: String(driver.id), no: String(driver.no) });
      });
      (this.dayMaxPointsRowsLocal || []).forEach((row) => {
        const id = row.driverId == null ? "" : String(row.driverId).trim();
        if (id && !map.has(id)) {
          map.set(id, { id, no: id });
        }
      });
      return [...map.values()].sort((a, b) => String(a.no).localeCompare(String(b.no), "zh"));
    },

    tripCostRows() {
      const paths = this.pathList || [];
      if (paths.length) {
        return paths.map((p) => ({ pathId: p.id, name: p.name }));
      }
      return Object.keys(this.config.tripCostByRoute || {})
        .map(Number)
        .filter((id) => !Number.isNaN(id))
        .sort((a, b) => a - b)
        .map((pathId) => ({ pathId, name: `路线${pathId}` }));
    }
  },

  mounted() {
    this.loadConfig();
    this.loadDriverOptions();
  },

  methods: {
    areaLabel(areaId) {
      const area = this.areaOptions.find((item) => item.id === areaId);
      return area ? `${area.name}(${areaId})` : `区域${areaId}`;
    },

    loadDriverOptions() {
      getDriverList({
        page: { current: 1, size: 3000 },
        param: { status: 1 }
      }).then((res) => {
        const data = res.data || {};
        this.driverOptions = data.records || [];
      });
    },

    loadConfig() {
      const reqId = ++this.loadSeq;
      this.loading = true;
      return getEvTunableConfig({ _hideLoading: true })
        .then((res) => {
          if (reqId !== this.loadSeq) return;
          const serverData = res.data;
          const needsPersist = hasUnpersistedMaps(serverData);
          this.config = this.normalizeConfig(mergeTunableWithSeed(serverData));
          this.syncWTimeRowsFromConfig();
          this.syncWLocByDateRowsFromConfig();
          this.syncWDateRowsFromConfig();
          this.syncDayMaxRowsFromConfig();
          this.wTimeInitialized = true;
          this.usingSeed = needsPersist;
          this.dirty = needsPersist;
        })
        .finally(() => {
          if (reqId === this.loadSeq) {
            this.loading = false;
          }
        });
    },

    normalizeConfig(data) {
      const src = data || {};
      const seed = createSeedTunableConfig();
      return {
        wLoc: this.normalizeNumberKeyMap(src.wLoc),
        rPtsByArea: this.normalizeNumberKeyMap(src.rPtsByArea),
        wTimeByHour: this.normalizeNumberKeyMap(src.wTimeByHour),
        wTimeByAreaHour: this.normalizeNestedNumberKeyMap(src.wTimeByAreaHour),
        roiBreakpoints: (src.roiBreakpoints || []).map((bp) => ({ minutes: bp.minutes, value: bp.value })),
        gammaBreakpoints: (src.gammaBreakpoints || []).map((bp) => ({ minutes: bp.minutes, value: bp.value })),
        betaBreakpoints: (src.betaBreakpoints || []).map((bp) => ({ minutes: bp.minutes, value: bp.value })),
        muFloor: src.muFloor != null ? src.muFloor : seed.muFloor,
        muCeil: src.muCeil != null ? src.muCeil : seed.muCeil,
        muNormalCars: src.muNormalCars != null ? src.muNormalCars : seed.muNormalCars,
        muHotCars: src.muHotCars != null ? src.muHotCars : seed.muHotCars,
        muSurplusBufferK: src.muSurplusBufferK != null ? src.muSurplusBufferK : seed.muSurplusBufferK,
        muSurplusBufferKByArea: this.normalizeNumberKeyMap(src.muSurplusBufferKByArea),
        muSurplusBinCapK: src.muSurplusBinCapK != null ? src.muSurplusBinCapK : seed.muSurplusBinCapK,
        muSurplusLambda: src.muSurplusLambda != null ? src.muSurplusLambda : seed.muSurplusLambda,
        muSurplusFloor: src.muSurplusFloor != null ? src.muSurplusFloor : seed.muSurplusFloor,
        unusedCarCost: src.unusedCarCost != null ? src.unusedCarCost : seed.unusedCarCost,
        tripCostByRoute: this.normalizeNumberKeyMap(src.tripCostByRoute),
        wLocByDate: this.normalizeDateAreaMap(src.wLocByDate),
        wDateWeekday: src.wDateWeekday != null ? src.wDateWeekday : seed.wDateWeekday,
        wDateWeekend: src.wDateWeekend != null ? src.wDateWeekend : seed.wDateWeekend,
        wDateHoliday: src.wDateHoliday != null ? src.wDateHoliday : seed.wDateHoliday,
        wDateByDate: this.normalizeStringKeyMap(src.wDateByDate),
        dayMaxPointsByDate: this.normalizeDayMaxPoints(src.dayMaxPointsByDate)
      };
    },

    normalizeDayMaxPoints(map) {
      const result = {};
      Object.keys(map || {}).forEach((dateKey) => {
        const date = this.parseDateKey(dateKey);
        if (!date) return;
        const byDriver = {};
        Object.keys(map[dateKey] || {}).forEach((driverId) => {
          const points = Number(map[dateKey][driverId]);
          if (driverId && !Number.isNaN(points) && points >= 0) {
            byDriver[String(driverId).trim()] = points;
          }
        });
        if (Object.keys(byDriver).length) {
          result[date] = byDriver;
        }
      });
      return result;
    },

    normalizeStringKeyMap(map) {
      const result = {};
      Object.keys(map || {}).forEach((key) => {
        if (key && map[key] != null) {
          result[key] = map[key];
        }
      });
      return result;
    },

    normalizeNumberKeyMap(map) {
      const result = {};
      Object.keys(map || {}).forEach((key) => {
        const n = Number(key);
        if (!Number.isNaN(n) && map[key] != null) {
          result[n] = map[key];
        }
      });
      return result;
    },

    normalizeNestedNumberKeyMap(map) {
      const result = {};
      Object.keys(map || {}).forEach((areaKey) => {
        const areaId = Number(areaKey);
        if (!Number.isNaN(areaId)) {
          result[areaId] = this.normalizeNumberKeyMap(map[areaKey]);
        }
      });
      return result;
    },

    syncWTimeRowsFromConfig() {
      const map = this.config.wTimeByHour || {};
      this.wTimeHourRowsLocal = Object.keys(map)
        .map(Number)
        .sort((a, b) => a - b)
        .map((hour) => ({
          uid: nextRowUid(),
          hour,
          weight: map[hour]
        }));

      const nested = this.config.wTimeByAreaHour || {};
      const rows = [];
      Object.keys(nested).forEach((areaKey) => {
        const areaId = Number(areaKey);
        const byHour = nested[areaKey] || {};
        Object.keys(byHour).forEach((hourKey) => {
          rows.push({
            uid: nextRowUid(),
            areaId,
            hour: Number(hourKey),
            weight: byHour[hourKey]
          });
        });
      });
      this.wTimeAreaHourRowsLocal = rows.sort((a, b) => a.areaId - b.areaId || a.hour - b.hour);
    },

    normalizeDateAreaMap(map) {
      const result = {};
      Object.keys(map || {}).forEach((dateKey) => {
        const date = this.parseDateKey(dateKey);
        if (!date) return;
        result[date] = this.normalizeNumberKeyMap(map[dateKey]);
      });
      return result;
    },

    syncWLocByDateRowsFromConfig() {
      const nested = this.config.wLocByDate || {};
      const rows = [];
      Object.keys(nested).sort().forEach((date) => {
        const byArea = nested[date] || {};
        Object.keys(byArea).forEach((areaKey) => {
          rows.push({
            uid: nextRowUid(),
            date,
            areaId: Number(areaKey),
            weight: byArea[areaKey]
          });
        });
      });
      this.wLocByDateRowsLocal = rows;
    },

    syncWLocByDateRowsToConfig() {
      const wLocByDate = {};
      this.wLocByDateRowsLocal.forEach((row) => {
        const date = this.parseDateKey(row.date);
        const areaId = this.parseIntVal(row.areaId);
        const weight = this.parseWeight(row.weight);
        if (!date || areaId == null || weight == null) return;
        if (!wLocByDate[date]) {
          wLocByDate[date] = {};
        }
        wLocByDate[date][areaId] = weight;
      });
      this.config.wLocByDate = wLocByDate;
    },

    syncWDateRowsFromConfig() {
      const map = this.config.wDateByDate || {};
      this.wDateOverrideRowsLocal = Object.keys(map)
        .sort()
        .map((date) => ({
          uid: nextRowUid(),
          date,
          weight: map[date]
        }));
    },

    syncWDateRowsToConfig() {
      const wDateByDate = {};
      this.wDateOverrideRowsLocal.forEach((row) => {
        const date = this.parseDateKey(row.date);
        const weight = this.parseWeight(row.weight);
        if (!date || weight == null) return;
        wDateByDate[date] = weight;
      });
      this.config.wDateByDate = wDateByDate;
    },

    syncDayMaxRowsFromConfig() {
      const nested = this.config.dayMaxPointsByDate || {};
      const rows = [];
      Object.keys(nested).sort().forEach((date) => {
        const byDriver = nested[date] || {};
        Object.keys(byDriver).forEach((driverId) => {
          rows.push({
            uid: nextRowUid(),
            date,
            driverId: String(driverId),
            points: byDriver[driverId]
          });
        });
      });
      this.dayMaxPointsRowsLocal = rows;
    },

    syncDayMaxRowsToConfig() {
      const dayMaxPointsByDate = {};
      this.dayMaxPointsRowsLocal.forEach((row) => {
        const date = this.parseDateKey(row.date);
        const driverId = row.driverId == null ? "" : String(row.driverId).trim();
        const points = this.parseIntVal(row.points);
        if (!date || !driverId || points == null || points < 0) return;
        if (!dayMaxPointsByDate[date]) {
          dayMaxPointsByDate[date] = {};
        }
        dayMaxPointsByDate[date][driverId] = points;
      });
      this.config.dayMaxPointsByDate = dayMaxPointsByDate;
    },

    parseDateKey(val) {
      if (!val || typeof val !== "string") return null;
      const date = val.trim();
      return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
    },

    syncWTimeRowsToConfig() {
      const wTimeByHour = {};
      this.wTimeHourRowsLocal.forEach((row) => {
        const hour = this.parseHour(row.hour);
        const weight = this.parseWeight(row.weight);
        if (hour == null || weight == null) return;
        wTimeByHour[hour] = weight;
      });
      this.config.wTimeByHour = wTimeByHour;

      const wTimeByAreaHour = {};
      this.wTimeAreaHourRowsLocal.forEach((row) => {
        const areaId = this.parseIntVal(row.areaId);
        const hour = this.parseHour(row.hour);
        const weight = this.parseWeight(row.weight);
        if (areaId == null || hour == null || weight == null) return;
        if (!wTimeByAreaHour[areaId]) {
          wTimeByAreaHour[areaId] = {};
        }
        wTimeByAreaHour[areaId][hour] = weight;
      });
      this.config.wTimeByAreaHour = wTimeByAreaHour;
    },

    parseIntVal(val) {
      if (val === "" || val == null) return null;
      const n = parseInt(val, 10);
      return Number.isNaN(n) ? null : n;
    },

    parseHour(val) {
      const n = this.parseIntVal(val);
      if (n == null || n < 0 || n > 23) return null;
      return n;
    },

    parseWeight(val) {
      if (val === "" || val == null) return null;
      const n = parseFloat(val);
      return Number.isNaN(n) ? null : n;
    },

    setWTimeHourField(index, field, rawVal) {
      const row = this.wTimeHourRowsLocal[index];
      if (!row) return;
      if (field === "hour") {
        this.$set(row, "hour", rawVal === "" ? "" : this.parseIntVal(rawVal));
      } else {
        this.$set(row, "weight", rawVal === "" ? "" : rawVal);
      }
      this.markDirty();
    },

    setWTimeAreaField(index, field, rawVal) {
      const row = this.wTimeAreaHourRowsLocal[index];
      if (!row) return;
      if (field === "areaId") {
        this.$set(row, "areaId", rawVal);
      } else if (field === "hour") {
        this.$set(row, "hour", rawVal === "" ? "" : this.parseIntVal(rawVal));
      } else {
        this.$set(row, "weight", rawVal === "" ? "" : rawVal);
      }
      this.markDirty();
    },

    copyNestedMap(source) {
      const result = {};
      if (!source) return result;
      Object.keys(source).forEach((areaKey) => {
        result[areaKey] = { ...(source[areaKey] || {}) };
      });
      return result;
    },

    buildPayload() {
      this.materializeAreaMaps();
      this.syncWTimeRowsToConfig();
      this.syncWLocByDateRowsToConfig();
      this.syncWDateRowsToConfig();
      this.syncDayMaxRowsToConfig();
      return {
        wLoc: this.toStringKeyMap(this.config.wLoc),
        rPtsByArea: this.toStringKeyMap(this.config.rPtsByArea),
        wTimeByHour: this.toStringKeyMap(this.config.wTimeByHour),
        wTimeByAreaHour: this.toNestedStringKeyMap(this.config.wTimeByAreaHour),
        roiBreakpoints: this.config.roiBreakpoints,
        gammaBreakpoints: this.config.gammaBreakpoints,
        betaBreakpoints: this.config.betaBreakpoints,
        muFloor: this.config.muFloor,
        muCeil: this.config.muCeil,
        muNormalCars: this.config.muNormalCars,
        muHotCars: this.config.muHotCars,
        muSurplusBufferK: this.config.muSurplusBufferK,
        muSurplusBufferKByArea: this.toStringKeyMap(this.config.muSurplusBufferKByArea),
        muSurplusBinCapK: this.config.muSurplusBinCapK,
        muSurplusLambda: this.config.muSurplusLambda,
        muSurplusFloor: this.config.muSurplusFloor,
        unusedCarCost: this.config.unusedCarCost,
        sellableRewardCars: 0,
        tripCostByRoute: this.toStringKeyMap(this.config.tripCostByRoute),
        sellableRewardByRoute: {},
        wLocByDate: this.toDateAreaStringMap(this.config.wLocByDate),
        wDateWeekday: this.config.wDateWeekday,
        wDateWeekend: this.config.wDateWeekend,
        wDateHoliday: this.config.wDateHoliday,
        wDateByDate: this.normalizeStringKeyMap(this.config.wDateByDate),
        dayMaxPointsByDate: this.normalizeDayMaxPoints(this.config.dayMaxPointsByDate)
      };
    },

    /** 保存前把表格展示值写入 config，避免仅 UI fallback 未落库 */
    materializeAreaMaps() {
      this.areaIds.forEach((areaId) => {
        if (this.config.wLoc[areaId] == null) {
          this.$set(this.config.wLoc, areaId, this.wLocValue(areaId));
        }
        if (this.config.rPtsByArea[areaId] == null) {
          this.$set(this.config.rPtsByArea, areaId, this.rPtsValue(areaId));
        }
      });
    },

    toStringKeyMap(map) {
      const result = {};
      Object.keys(map || {}).forEach((key) => {
        const val = map[key];
        if (val != null) {
          result[String(key)] = val;
        }
      });
      return result;
    },

    toDateAreaStringMap(map) {
      const result = {};
      Object.keys(map || {}).forEach((date) => {
        const parsed = this.parseDateKey(date);
        if (!parsed) return;
        result[parsed] = this.toStringKeyMap(map[date]);
      });
      return result;
    },

    toNestedStringKeyMap(map) {
      const result = {};
      Object.keys(map || {}).forEach((areaKey) => {
        result[String(areaKey)] = this.toStringKeyMap(map[areaKey]);
      });
      return result;
    },

    wLocValue(areaId) {
      return this.config.wLoc[areaId] != null ? this.config.wLoc[areaId] : 1;
    },

    muSurplusBufferKValue(areaId) {
      const map = this.config.muSurplusBufferKByArea || {};
      if (map[areaId] != null) {
        return map[areaId];
      }
      return this.config.muSurplusBufferK != null ? this.config.muSurplusBufferK : 2;
    },

    updateMuSurplusBufferK(areaId, value) {
      if (!this.config.muSurplusBufferKByArea) {
        this.$set(this.config, "muSurplusBufferKByArea", {});
      }
      const fallback = this.config.muSurplusBufferK != null ? this.config.muSurplusBufferK : 2;
      if (value == null || value === "") {
        this.$delete(this.config.muSurplusBufferKByArea, areaId);
      } else if (Number(value) === Number(fallback)) {
        this.$delete(this.config.muSurplusBufferKByArea, areaId);
      } else {
        this.$set(this.config.muSurplusBufferKByArea, areaId, value);
      }
      this.markDirty();
    },

    tripCostValue(pathId) {
      if (!this.config.tripCostByRoute) {
        return undefined;
      }
      return this.config.tripCostByRoute[pathId];
    },

    setTripCost(pathId, value) {
      if (!this.config.tripCostByRoute) {
        this.$set(this.config, "tripCostByRoute", {});
      }
      if (value == null || value === "") {
        this.$delete(this.config.tripCostByRoute, pathId);
      } else {
        this.$set(this.config.tripCostByRoute, pathId, value);
      }
      this.markDirty();
    },

    rPtsValue(areaId) {
      return this.config.rPtsByArea[areaId] != null ? this.config.rPtsByArea[areaId] : 30;
    },

    updateWLoc(areaId, weight) {
      if (weight == null) return;
      this.$set(this.config.wLoc, areaId, weight);
      this.markDirty();
    },

    updateRPts(areaId, value) {
      if (value == null) return;
      this.$set(this.config.rPtsByArea, areaId, value);
      this.markDirty();
    },

    markDirty() {
      this.dirty = true;
    },

    exportConfig() {
      const run = () => {
        getEvTunableConfig({ _hideLoading: true }).then((res) => {
          const payload = res.data || {};
          const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          const day = new Date();
          const stamp = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
          link.href = url;
          link.download = `ev-config-${stamp}.json`;
          link.click();
          URL.revokeObjectURL(url);
          this.$message.success("已导出当前生效配置");
        });
      };
      if (!this.dirty) {
        run();
        return;
      }
      this.$confirm("页面上有未保存的修改。导出的是服务器上已生效的配置。", "导出配置", {
        confirmButtonText: "导出已生效配置",
        cancelButtonText: "取消",
        type: "warning"
      }).then(run).catch(() => { });
    },

    pickImportFile() {
      const input = this.$refs.configFile;
      if (!input) return;
      input.value = "";
      input.click();
    },

    onImportFile(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        let raw;
        try {
          raw = JSON.parse(reader.result);
        } catch (e) {
          this.$message.error("文件不是合法的 JSON");
          return;
        }
        const payload = pickTunableConfig(raw);
        if (!payload) {
          this.$message.error("文件里没有可导入的 EV 配置");
          return;
        }
        this.$confirm("导入后会覆盖本地当前配置并立即生效。路线路时、票价、区域最低积分仍按本地路线表计算。", "导入配置", {
          confirmButtonText: "导入并生效",
          cancelButtonText: "取消",
          type: "warning"
        }).then(() => {
          this.saving = true;
          applyEvTunableConfig(payload)
            .then(() => {
              this.$message.success("配置已导入并生效");
              return this.loadConfig();
            })
            .finally(() => {
              this.saving = false;
            });
        }).catch(() => { });
      };
      reader.readAsText(file);
    },

    saveConfig() {
      this.saving = true;
      const wTimeHourSnapshot = this.wTimeHourRowsLocal.map((row) => ({ ...row }));
      const wTimeAreaSnapshot = this.wTimeAreaHourRowsLocal.map((row) => ({ ...row }));
      const wLocByDateSnapshot = this.wLocByDateRowsLocal.map((row) => ({ ...row }));
      const wDateSnapshot = this.wDateOverrideRowsLocal.map((row) => ({ ...row }));
      const dayMaxSnapshot = this.dayMaxPointsRowsLocal.map((row) => ({ ...row }));
      const payload = this.buildPayload();
      return applyEvTunableConfig(payload)
        .then((res) => {
          this.mergeConfigAfterApply(res.data, wTimeHourSnapshot, wTimeAreaSnapshot, wLocByDateSnapshot, wDateSnapshot, dayMaxSnapshot);
          this.usingSeed = false;
          this.dirty = false;
          this.$message.success("配置已应用并保存");
        })
        .finally(() => {
          this.saving = false;
        });
    },

    mergeConfigAfterApply(serverData, wTimeHourRows, wTimeAreaRows, wLocByDateRows, wDateRows, dayMaxRows) {
      this.config = this.normalizeConfig(serverData);
      this.wTimeHourRowsLocal = wTimeHourRows;
      this.wTimeAreaHourRowsLocal = wTimeAreaRows;
      this.wLocByDateRowsLocal = wLocByDateRows || [];
      this.wDateOverrideRowsLocal = wDateRows || [];
      this.dayMaxPointsRowsLocal = dayMaxRows || [];
      this.syncWTimeRowsToConfig();
      this.syncWLocByDateRowsToConfig();
      this.syncWDateRowsToConfig();
      this.syncDayMaxRowsToConfig();
    },

    addWTimeHour() {
      const used = this.wTimeHourRowsLocal.map((r) => this.parseHour(r.hour)).filter((h) => h != null);
      let hour = 17;
      while (used.includes(hour) && hour < 24) hour += 1;
      if (hour >= 24) hour = 0;
      while (used.includes(hour) && hour < 24) hour += 1;
      this.wTimeHourRowsLocal = [
        ...this.wTimeHourRowsLocal,
        { uid: nextRowUid(), hour, weight: 1 }
      ];
      this.markDirty();
    },

    removeWTimeHour(index) {
      this.wTimeHourRowsLocal = this.wTimeHourRowsLocal.filter((_, i) => i !== index);
      this.markDirty();
    },

    addWTimeAreaHour() {
      const areaId = this.areaIds[0] || 1;
      const usedHours = this.wTimeAreaHourRowsLocal
        .filter((r) => r.areaId === areaId)
        .map((r) => this.parseHour(r.hour))
        .filter((h) => h != null);
      let hour = 17;
      while (usedHours.includes(hour) && hour < 24) hour += 1;
      this.wTimeAreaHourRowsLocal = [
        ...this.wTimeAreaHourRowsLocal,
        { uid: nextRowUid(), areaId, hour, weight: 1 }
      ];
      this.markDirty();
    },

    removeWTimeAreaHour(index) {
      this.wTimeAreaHourRowsLocal = this.wTimeAreaHourRowsLocal.filter((_, i) => i !== index);
      this.markDirty();
    },

    addWLocByDate() {
      this.wLocByDateRowsLocal = [
        ...this.wLocByDateRowsLocal,
        { uid: nextRowUid(), date: "", areaId: this.areaIds[0] || 1, weight: 1 }
      ];
      this.markDirty();
    },

    setWLocByDateField(index, field, rawVal) {
      const row = this.wLocByDateRowsLocal[index];
      if (!row) return;
      if (field === "areaId") {
        this.$set(row, "areaId", rawVal);
      } else if (field === "weight") {
        this.$set(row, "weight", rawVal === "" ? "" : rawVal);
      } else {
        this.$set(row, field, rawVal == null ? "" : rawVal);
      }
      this.markDirty();
    },

    removeWLocByDate(index) {
      this.wLocByDateRowsLocal = this.wLocByDateRowsLocal.filter((_, i) => i !== index);
      this.markDirty();
    },

    addWDateOverride() {
      this.wDateOverrideRowsLocal = [
        ...this.wDateOverrideRowsLocal,
        { uid: nextRowUid(), date: "", weight: 1 }
      ];
      this.markDirty();
    },

    setWDateOverrideField(index, field, rawVal) {
      const row = this.wDateOverrideRowsLocal[index];
      if (!row) return;
      this.$set(row, field, rawVal == null ? "" : rawVal);
      this.markDirty();
    },

    removeWDateOverride(index) {
      this.wDateOverrideRowsLocal = this.wDateOverrideRowsLocal.filter((_, i) => i !== index);
      this.markDirty();
    },

    addDayMaxPoints() {
      this.dayMaxPointsRowsLocal = [
        ...this.dayMaxPointsRowsLocal,
        { uid: nextRowUid(), date: "", driverId: "", points: 200 }
      ];
      this.markDirty();
    },

    setDayMaxPointsField(index, field, rawVal) {
      const row = this.dayMaxPointsRowsLocal[index];
      if (!row) return;
      this.$set(row, field, rawVal == null ? "" : rawVal);
      this.markDirty();
    },

    removeDayMaxPoints(index) {
      this.dayMaxPointsRowsLocal = this.dayMaxPointsRowsLocal.filter((_, i) => i !== index);
      this.markDirty();
    },

    onReset() {
      this.$confirm("确认恢复 W_loc / W_time / r_pts / θ / γ / β 为前端默认种子值并写入服务端？司机按日积分上限会保留。", "恢复默认", {
        type: "warning"
      }).then(() => {
        this.resetting = true;
        const dayMaxSnapshot = this.dayMaxPointsRowsLocal.map((row) => ({ ...row }));
        const seed = createSeedTunableConfig();
        this.config = this.normalizeConfig(seed);
        this.syncWTimeRowsFromConfig();
        this.syncWLocByDateRowsFromConfig();
        this.syncWDateRowsFromConfig();
        this.dayMaxPointsRowsLocal = dayMaxSnapshot;
        const payload = this.buildPayload();
        return applyEvTunableConfig(payload)
          .then((res) => {
            this.config = this.normalizeConfig(res.data);
            this.syncWTimeRowsFromConfig();
            this.syncWLocByDateRowsFromConfig();
            this.syncWDateRowsFromConfig();
            this.dayMaxPointsRowsLocal = dayMaxSnapshot;
            this.syncDayMaxRowsToConfig();
            this.usingSeed = false;
            this.dirty = false;
            this.$message.success("已恢复默认配置");
          })
          .finally(() => {
            this.resetting = false;
          });
      });
    }
  }
};
</script>

<style lang="less" scoped>
.ev-config-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.config-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.hint {
  font-size: 12px;
  color: #909399;
}

.form-hint {
  margin-left: 8px;
  font-size: 12px;
  color: #909399;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.config-file {
  display: none;
}

.config-scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-right: 4px;
}

.section-desc {
  margin: 0 0 8px;
  font-size: 12px;
  color: #909399;
}

.section-actions {
  margin-bottom: 8px;
}

.compact-table {
  max-width: 520px;
}

::v-deep .el-collapse-item__header {
  font-size: 13px;
  font-weight: 600;
  height: 36px;
  line-height: 36px;
}

::v-deep .el-collapse-item__content {
  padding-bottom: 12px;
}
</style>
